<template>
  <div data-module="work-main-articles" class="tablet"
    :style="mainContainerStyles"
  >
    <div class="background"
      :style="bgStyles"
    />
    <div class="component-layout" ref="mainContainer">
      <div class="article-tile-container"
        v-for="(record, idx) in tileRecords"
        :key="idx"
        :style="record.style"
      >
        <category-page-tile
          v-if="record.data.isCategory === true"
          :record="record.data"
          :height="record.height"
          :width="record.width"
          :colSpan="record.colSpan"
          ref="tileRefs"
        />
        <static-matter-tile
          v-else-if="record.data.isMatterArticle === true && isDesktop"
          :record="record.data"
          :height="record.height"
          :width="record.width"
          :colSpan="record.colSpan"
          ref="tileRefs"
        />

        <reveal-work-tile
          v-else-if="isTablet"
          :record="record.data"
          ref="tileRefs"
        />
        
        <static-work-tile
          v-else
          :record="record.data"
          :height="record.height"
          :width="record.width"
          :colSpan="record.colSpan"
          ref="tileRefs"
        />
      </div>
    </div>
  </div>
</template>

<script>
/*
  supported aspect ratios:
  (colSpan=1)
    landscape: 16:9
    small: 4:3
    square: 1:1
    medium: 3:4
    portrait / tall: 9:16
  (colSpan=2)
    <any>: 16:9
*/
// Libs
import Vue from 'vue';

// Components
import RevealWorkTile from '@/components/article-tile/ContentRevealTile';
import CategoryPageTile from '@/components/article-tile/CategoryPageTile';
import StaticMatterTile from '@/components/article-tile/ContentStaticMatterTile';
import StaticWorkTile from '@/components/article-tile/ContentStaticTile';

// Constants & Utils
import { objPropsToPixels } from '@/utils/data-utils';
import {
  AspectRatios,
  CategoryTileDims,
  DefaultAspectRatio,
  MaxBrowserWidth,
  WorkImageOffsets,
} from '@/app-constants';

export default {

  props: {
    articles: Array,
  },

  components: {
    CategoryPageTile,
    RevealWorkTile,
    StaticMatterTile,
    StaticWorkTile,
  },

  data() {
    return {

      // constants for layout
      colCount: 12,
      gapWidth: 26,

      // track dimensions of main tile container
      mainWidth: 0,
      mainHeight: 0,

      // track occupied height of columns
      col1Height: 0,
      col2Height: 0,
      col3Height: 0,

      // offset for each tile, if it's needed
      yDimOffset: [],

    };
  },

  computed: {

    currentBrowserWidth() {
      return this.$store.state.width;
    },

    isDesktop() {
      return this.$store.getters.isDesktop;
    },

    isTablet() {
      return this.$store.getters.isTablet;
    },

    singleColumnWidth() {
      const { col1Start, col4End } = this;
      const value = col4End - col1Start;

       // initial values are < 0, so clean up
      return Math.max(0, value);
    },

    /*
      calculate grid column width
      using current browser width & gap
    */
    colWidth() {
      const { colCount, gapWidth, mainWidth } = this;
      const totalGap = gapWidth * (colCount - 1);
      const colWidth = (mainWidth - totalGap) / colCount;
      return colWidth;
    },

    /*
      helpers for column starts & ends
    */
    col1Start() {
      return 0;
    },

    col4End() {
      const col = 4;
      const { colWidth, gapWidth } = this;
      return (col * colWidth) + ((col - 1) * gapWidth);
    },

    col5Start() {
      const col = 5;
      const { colWidth, gapWidth } = this;
      return (col - 1) * (colWidth + gapWidth);
    },

    col8End() {
      const col = 8;
      const { colWidth, gapWidth } = this;
      return (col * colWidth) + ((col - 1) * gapWidth);
    },

    col9Start() {
      const col = 9;
      const { colWidth, gapWidth } = this;
      return (col - 1) * (colWidth + gapWidth);
    },

    col12End() {
      const col = 12;
      const { colWidth, gapWidth } = this;
      return (col * colWidth) + ((col - 1) * gapWidth);
    },

    /*
      processing incoming work records
      {
        style: {
          top
          left
          width
          height
        }
        data: <tile data>
        height: <number>
        width: <number>
      }
    */
    tileRecords() {
      const { articles, isDesktop } = this;
      this.resetTileLayoutVars();

      const recs = articles.map((record, idx) => {
        const { colSpan } = record;
        const tileAdjust = this.getTileHeightAdjustment(idx);
        const style = this.calcTileContainerStyles({ record, tileAdjust });
        const height = style.height - tileAdjust;
        const { width } = style;

        // Tile Record
        return {
          data: record,
          style: objPropsToPixels(style),

          // sent to tile
          height,
          width,
          colSpan,
        };
      });

      // after layout calcs, find bounding container height
      this.mainHeight = this.calcMainContainerHeight();

      if (isDesktop) {
        // see if children need some more room
        this.requestChildUpdate();
      }

      return recs;
    },

    mainContainerStyles() {
      const { mainHeight } = this;
      return {
        height: `${mainHeight}px`,
      };
    },

    bgStyles() {
      const { currentBrowserWidth } = this;

      // calculate width
      let offset = 0;
      if (currentBrowserWidth > MaxBrowserWidth) {
        offset = (currentBrowserWidth - MaxBrowserWidth) * 0.5;
      }
      const style = {
        marginLeft: `-${offset}px`,
      };
      return style;
    },

  },

  methods: {

    /*
      lookup child's required height adjustment
    */
    getTileHeightAdjustment(idx) {
      const { isDesktop, yDimOffset } = this;
      if (isDesktop) {
        return yDimOffset[idx] || 0;
      }
      return 0;
    },

    /*
      calculate the height of a tile
      based on responsive value & aspect ratio
    */
    calcSingleTileHeight(props) {
      const { aspect, colSpan } = props;
      const { desktop } = CategoryTileDims;
      const sCW = this.singleColumnWidth;
      const ar169 = AspectRatios['16:9'];
      const ar43 = AspectRatios['4:3'];
      const ar34 = AspectRatios['3:4'];
      const ar916 = AspectRatios['9:16'];
      const ar72 = AspectRatios['7:2'];

      // tablet or desktop 2-col
      if (this.isTablet) {
        return sCW; // square
      } else if (colSpan === 2 && aspect === 'category-tile') {
        return (((2 * sCW) + desktop.gridGap) / ar72); // two-col item
      } else if (colSpan === 2) {
        return ((2 * sCW) / ar169); // two-col item, 16:9
      }

      // desktop other aspects
      switch (aspect) {
        case 'landscape':
          return (sCW / ar169);
        case 'small':
          return (sCW / ar43);
        case 'medium':
          return (sCW / ar34);
        case 'portrait':
        case 'tall':
          return (sCW / ar916);
        case 'square':
        default:
          return sCW; // square
      }
    },

    resetTileLayoutVars() {
      this.col1Height = 0;
      this.col2Height = 0;
      this.col3Height = 0;
    },

    calcMainContainerHeight() {
      const { col1Height, col2Height, col3Height } = this;
      // padding matches CSS declaration
      const padding = 20;
      return Math.max(col1Height, col2Height, col3Height) + (padding * 2);
    },

    /*
      lookup a tile's artistic margin
    */
    getTileTopMargin(record) {
      const { tileOffset } = record;
      const { isTablet } = this;

      if (isTablet) {
        return 0;
      }
      return WorkImageOffsets[tileOffset] || 0;
    },

    /*
      return container styles
    */
    calcTileContainerStyles(props) {
      const { record, tileAdjust } = props;
      const { colSpan, image } = record;
      const aspect = image.aspect || DefaultAspectRatio;

      const { width, height } = this.calcTileBasicDimensions({ colSpan, aspect });

      const tileHeight = height + tileAdjust;
      const tileMargin = this.getTileTopMargin(record);

      const config = {
        colSpan,
        tileMargin,
        tileHeight,
      };
      const { top, left } = this.calcTileContainerPlacement(config);

      return {
        top,
        left,
        height: tileHeight,
        width,
      };
    },

    calcTileBasicDimensions(props) {
      const { colSpan, aspect } = props;
      const { col1Start, col4End, col8End } = this;
      let width;
      let height;

      const config = {
        aspect,
        colSpan,
      };
      switch (colSpan) {
        case 2:
          width = col8End - col1Start;
          height = this.calcSingleTileHeight(config);
          break;
        case 1:
        default:
          width = col4End - col1Start;
          height = this.calcSingleTileHeight(config);
      }
      return { width, height };
    },

    calcTileContainerPlacement(config) {
      const { colSpan } = config;
      if (colSpan === 1) {
        return this.calc1ColTilePlacement(config);
      }
      return this.calc2ColTilePlacement(config);
    },

    calc1ColTilePlacement(config) {
      const { col1Start, col5Start, col9Start, gapWidth } = this;
      let { col1Height, col2Height, col3Height } = this;
      let top;
      let left;

      const { tileMargin, tileHeight } = config;

      // figure out column with best-fit
      const arr = [
        { key: 'col-1', val: col1Height },
        { key: 'col-2', val: col2Height },
        { key: 'col-3', val: col3Height },
      ];
      arr.sort(function(a, b) {
        return a.val - b.val;
      });
      const { key: bestColKey } = arr[0];

      switch (bestColKey) {
        case 'col-1':
          if (col1Height !== 0) {
            col1Height += gapWidth;
          }
          col1Height += tileMargin;

          top = col1Height;
          left = col1Start;
          col1Height += tileHeight;
          this.col1Height = col1Height;
          break;
        case 'col-2':
          if (col2Height !== 0) {
            col2Height += gapWidth;
          }
          col2Height += tileMargin;

          top = col2Height;
          left = col5Start;
          col2Height += tileHeight;
          this.col2Height = col2Height;
          break;
        case 'col-3':
          if (col3Height !== 0) {
            col3Height += gapWidth;
          }
          col3Height += tileMargin;

          top = col3Height;
          left = col9Start;
          col3Height += tileHeight;
          this.col3Height = col3Height;
          break;
        default:
          console.warn('calc1ColTilePlacement: Default Case');
          break;
      }

      return { top, left };
    },

    /*
      2-column tile can only be placed in Col 1 or Col 2
    */
    calc2ColTilePlacement(config) {
      const { col1Start, col5Start, gapWidth } = this;
      const { col1Height, col2Height, col3Height } = this;
      let top;
      let left;

      const { tileMargin, tileHeight } = config;

      // figure out column with best-fit
      const Col12Max = Math.max(col1Height, col2Height);
      const Col23Max = Math.max(col2Height, col3Height);
      // calculate trapped whitespace
      const trap12 = Math.max((Col12Max - col1Height), (Col12Max - col2Height));
      const trap23 = Math.max((Col23Max - col2Height), (Col23Max - col3Height));
      const bestColKey = (trap12 <= trap23) ? 'col-12' : 'col-23';

      let tmp;
      switch (bestColKey) {
        case 'col-12':
          tmp = Col12Max;
          if (Col12Max !== 0) {
            tmp += gapWidth;
          }
          tmp += tileMargin;

          top = tmp;
          left = col1Start;
          tmp += tileHeight;
          this.col1Height = tmp;
          this.col2Height = tmp;
          break;
        case 'col-23':
          tmp = Col23Max;

          if (Col23Max !== 0) {
            tmp += gapWidth;
          }
          tmp += tileMargin;

          top = tmp;
          left = col5Start;
          tmp += tileHeight;
          this.col2Height = tmp;
          this.col3Height = tmp;
          break;
        default:
          console.warn('calc2ColTilePlacement: Default Case');
          break;
      }

      return { top, left };
    },

    calcMainContainerWidth() {
      const el = this.$refs.mainContainer;
      return (el) ? el.clientWidth : 0;
    },

    childUpdate() {
      const { isDesktop, yDimOffset } = this;
      if (!isDesktop) {
        // another safety, not on tablet/mobile tiles
        return;
      }
      const children = this.$refs.tileRefs;
      const arr = [];
      let needsReset = false;
      children.forEach(function(child, idx) {
        const value = child.calculateTileAdjustment();
        if (yDimOffset[idx] !== value) {
          needsReset = true;
        }
        arr.push(value);
      });
      if (needsReset) {
        this.yDimOffset = arr;
      }
    },

    measureLayout() {
      this.mainWidth = this.calcMainContainerWidth();
    },

    requestChildUpdate() {
      Vue.nextTick()
        .then(() => this.childUpdate());
    },

    requestMeasureLayout() {
      Vue.nextTick()
        .then(() => this.measureLayout());
    },

  },

  mounted() {
    this.requestMeasureLayout();
  },

  watch: {

    mainWidth(/* newVal, oldVal */) {
      // handle browser resize
      this.requestMeasureLayout();
    },

    currentBrowserWidth(/* newVal, oldVal */) {
      this.requestMeasureLayout();
    },

  },
};
</script>


<style lang="scss" scoped>
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';

$width: $layout--optimal-desktop - (65*2);
$layout--percentage: $width / $layout--optimal-desktop * 100%;

// mobile not necessary, this module not rendered on mobile

[data-module="work-main-articles"] {
  width: 100%;

  // padding matches var in calcMainContainerHeight()
  // padding: rem(40 * 1px) 0;

  // media query props
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }

  .background {
    position: absolute;
    grid-column: 1 / span 3;
    background-color: $color--quaternary-midtone;
    height: 100%;
    width: 100vw;
  }

  .component-layout {
    // media query props
    // mobile not necessary, this module not rendered on mobile
    height: 100%;

    @include ngMq($nbp--tablet, up) {
      margin: 0 rem($grid-padding-tablet * 1px);
    }

    @include ngMq($nbp--desktop, up) {
      margin: 0 rem($grid-padding-desktop * 1px);
    }

    .article-tile-container {
      position: absolute; // free-form layout
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/work/WorkArticlesTablet.vue